import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FaqSection } from "@/components/FaqSection";
import { BottomCta } from "@/components/BottomCta";
import { JsonLd } from "@/components/JsonLd";
import { localBusinessEntity, type BreadcrumbItem, type FaqItem } from "@/lib/schema";

type HeroProps = React.ComponentProps<typeof PageHero>;

type Props = {
  hero: HeroProps;
  breadcrumbs: BreadcrumbItem[];
  children: React.ReactNode;
  faqs?: FaqItem[];
  faqTitle?: string;
  faqSubtitle?: string;
  /** Hide the bottom CTA band when the page ends with its own contact form. */
  showBottomCta?: boolean;
  bottomCta?: React.ComponentProps<typeof BottomCta>;
};

/**
 * Shared inner-page shell: short hero → breadcrumbs → content → optional FAQ → bottom CTA.
 * Matches homepage colors, fonts, spacing, and button styles.
 */
export function InnerPage({
  hero,
  breadcrumbs,
  children,
  faqs,
  faqTitle,
  faqSubtitle,
  showBottomCta = true,
  bottomCta,
}: Props) {
  return (
    <>
      {/* Same LocalBusiness @id / name / phone / URL as the homepage graph. */}
      <JsonLd data={{ "@context": "https://schema.org", ...localBusinessEntity() }} />
      <PageHero {...hero} />
      <Breadcrumbs items={breadcrumbs} />
      {children}
      {faqs && faqs.length > 0 && (
        <FaqSection faqs={faqs} title={faqTitle} subtitle={faqSubtitle} />
      )}
      {showBottomCta && <BottomCta {...bottomCta} />}
    </>
  );
}
